/**
 * Modal — accessible dialog primitive.
 *
 * Behaviour:
 * - Renders nothing when `open` is `false` (`AnimatePresence` handles exit
 *   animations).
 * - The dialog has `role="dialog"`, `aria-modal="true"`, and is labelled by
 *   the `<h2>` title via `aria-labelledby` (Requirement 10.2, 10.3).
 * - Focus is moved into the dialog on open and restored to the previously
 *   focused element on close.
 * - `Escape` and a click on the backdrop both invoke `onClose`. Clicks on
 *   the dialog itself do not bubble to the backdrop.
 * - Touch: a downward swipe gesture on the dialog (≥ 80 px) dismisses it
 *   (Task 20.2, Requirement 9.5). Listeners are passive so they never
 *   interfere with text selection inside the dialog body.
 * - Framer Motion respects `prefers-reduced-motion: reduce` natively, so
 *   users who opt out of motion get the opacity transition only.
 *
 * The component is intentionally minimal — it does not portal into
 * `document.body`. Callers who need portalling can wrap it with
 * `createPortal`.
 */

import { useEffect, useRef } from 'react';
import { useId } from 'react';
import type { MouseEvent, ReactElement, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { useSwipeGesture } from '../../infra/swipeGesture';

import styles from './Modal.module.css';

type PrimaryTone = 'default' | 'destructive';

interface PrimaryAction {
  label: string;
  onClick: () => void;
  /** `'destructive'` paints the primary button red. */
  tone?: PrimaryTone;
}

interface SecondaryAction {
  label: string;
  onClick: () => void;
}

interface ModalProps {
  /** When `true`, the dialog is mounted, focus-trapped, and animated in. */
  open: boolean;
  /** Visible dialog title — also serves as the accessible name. */
  title: string;
  /** Invoked on Escape, backdrop click, or programmatic dismissal. */
  onClose: () => void;
  /** Body content rendered between the title and the action row. */
  children: ReactNode;
  /** Primary (confirm) action button. */
  primaryAction?: PrimaryAction;
  /** Secondary (cancel) action button rendered to the left of primary. */
  secondaryAction?: SecondaryAction;
}

export function Modal({
  open,
  title,
  onClose,
  children,
  primaryAction,
  secondaryAction,
}: ModalProps): ReactElement {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // Stable, unique id so multiple modals on the same page each get a distinct
  // `aria-labelledby` target. `useId` is SSR-safe.
  const titleId = useId();

  // Swipe-down dismiss on touch devices (Task 20.2). The hook is
  // `enabled: open` so it does not stay attached after the modal
  // closes; `threshold: 80` keeps it from firing on small jitter.
  useSwipeGesture(dialogRef, {
    onSwipeDown: () => onClose(),
    threshold: 80,
    enabled: open,
  });

  // Open-side effects:
  //   - capture the previously focused element so we can restore it on close
  //   - move focus into the dialog
  //   - listen for Escape to close
  // Cleanup is guarded so we never restore focus when the modal was never
  // actually opened.
  useEffect(() => {
    if (!open) return;

    const previousFocus =
      typeof document !== 'undefined'
        ? (document.activeElement as HTMLElement | null)
        : null;

    // Defer focusing to the next microtask so Framer Motion has mounted the
    // dialog node before we try to focus it.
    const raf =
      typeof window !== 'undefined'
        ? window.requestAnimationFrame(() => {
            dialogRef.current?.focus();
          })
        : 0;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (typeof window !== 'undefined') {
        window.cancelAnimationFrame(raf);
      }
      // Best-effort focus restoration. If the previously focused element has
      // since unmounted, just skip — the browser will pick a sensible default.
      previousFocus?.focus?.();
    };
  }, [open, onClose]);

  const handleBackdropClick = (): void => {
    onClose();
  };

  const stopPropagation = (event: MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  const primaryClasses = [
    styles.btn,
    styles.btnPrimary,
    primaryAction?.tone === 'destructive' ? styles.btnDestructive : '',
  ]
    .filter(Boolean)
    .join(' ');

  const secondaryClasses = [styles.btn, styles.btnSecondary].filter(Boolean).join(' ');

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="modal-backdrop"
          className={styles.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={handleBackdropClick}
          data-testid="modal-backdrop"
        >
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={styles.modal}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={stopPropagation}
            data-testid="modal"
          >
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            <div className={styles.body}>{children}</div>
            {(primaryAction ?? secondaryAction) ? (
              <div className={styles.actions}>
                {secondaryAction ? (
                  <button
                    type="button"
                    onClick={secondaryAction.onClick}
                    className={secondaryClasses}
                  >
                    {secondaryAction.label}
                  </button>
                ) : null}
                {primaryAction ? (
                  <button
                    type="button"
                    onClick={primaryAction.onClick}
                    className={primaryClasses}
                  >
                    {primaryAction.label}
                  </button>
                ) : null}
              </div>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
